import { Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { createNewWitness, ICaseDetailsInputs } from 'API';
import {
  PhoneNumber,
  PaperCard,
  ControllerSelect,
  CustomCheckbox,
  FileUpload,
} from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, FileCategories } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { useCallback, useContext, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateError } from 'Regex';
import {
  validateLettersAndNumbersOnly,
  validateLettersOnly,
} from 'Utils/validateLettersAndNumbersOnly';
import { IndexContext } from '../CaseContext';
import { SummaryCollapseBox } from './SummaryCollapseBox';
import { styleTextField } from 'Theme';
import { LinkDownload } from 'Components/LinkDownload';
import { isFileExist } from 'Utils/isFileExist';
import { textFieldShrink } from 'Utils';

interface IWitnessCard {
  index: number;
  caseId: number | undefined;
  deleteWitness: () => void;
  isOpen: boolean;
  toggleCard: () => void;
}
export const WitnessCard = (props: IWitnessCard) => {
  const { index, baseName = '' } = useContext(IndexContext);
  const {
    register,
    formState: { errors },
    clearErrors,
    setValue,
  } = useFormContext<ICaseDetailsInputs>();

  const [witnessesType, fileCategories] = useAppSelector(
    ({
      generalContent: {
        generalContent: { witnessTypes, fileCategories },
      },
    }) => [witnessTypes, fileCategories]
  );

  const filesList = useAppSelector(({ file: { files } }) => files);

  const isDesktop = useIsDesktop();

  const witnessErrorsArray = (() => {
    const caseDetailsIndex = (baseName.split('.')[1] as unknown as number) || 0;
    if (
      errors.caseDetails &&
      errors.caseDetails[caseDetailsIndex] &&
      errors.caseDetails[caseDetailsIndex].witnesses
    ) {
      return errors.caseDetails[caseDetailsIndex].witnesses;
    } else {
      return undefined;
    }
  })();

  const witnessError =
    witnessErrorsArray !== undefined ? witnessErrorsArray[index] : undefined;

  const witnessBaseName = `${baseName}.${index}`;

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const [hasNoCivilAddress, hasNoWitnessDocument, witnessId, civilAddress] =
    useWatch({
      name: [
        `${witnessBaseName}.hasNoCivilAddress`,
        `${witnessBaseName}.hasNoWitnessDocument`,
        `${witnessBaseName}.id`,
        `${witnessBaseName}.civilAddress` as `caseDetails.0.witnesses.0.civilAddress`,
      ],
    });

  const initFirstWitness = useCallback(async () => {
    if (props.caseId && !witnessId) {
      const result = await createNewWitness(props.caseId);
      if (result) {
        setValue(
          `${witnessBaseName}.id` as `caseDetails.0.witnesses.0.id`,
          result
        );
      }
    }
  }, []);

  useEffect(() => {
    initFirstWitness();
  }, [initFirstWitness]);

  useEffect(() => {
    if (hasNoCivilAddress) {
      setValue(
        `${witnessBaseName}.civilAddress` as `caseDetails.0.witnesses.0.civilAddress`,
        ''
      );
      clearErrors(
        `${witnessBaseName}.civilAddress` as `caseDetails.0.witnesses.0.civilAddress`
      );
    }
  }, [clearErrors, setValue, witnessBaseName, hasNoCivilAddress]);

  return (
    <PaperCard
      index={props.index}
      remove={props.deleteWitness}
      text={<SummaryCollapseBox />}
      isOpen={props.isOpen}
      setCurrentOpenCard={props.toggleCard}
    >
      <Grid
        container
        gap={4}
        sx={{
          '& .MuiFormHelperText-root': { backgroundColor: '#F8F8F8', m: 0 },
        }}
      >
        <Typography
          color='primary'
          sx={{
            fontWeight: 'bold',
            width: '100%',
            paddingLeft: isDesktop ? '0' : '40px',
            pt: isDesktop ? '0' : '8px',
          }}
        >
          {dictionary.sixthSectionWitnessTitle}
        </Typography>
        <TextField
          role={Boolean(witnessError?.firstName) ? 'alert' : undefined}
          inputProps={{
            maxLength: '20',
          }}
          id={`${dictionary.sixthSectionWitnessFirstInput}-${index}`}
          error={Boolean(witnessError?.firstName)}
          helperText={
            Boolean(witnessError?.firstName?.message) &&
            generateError(
              dictionary.sixthSectionWitnessFirstInput,
              witnessError?.firstName?.message!
            )
          }
          {...register(
            `${witnessBaseName}.firstName` as `caseDetails.0.witnesses.0.firstName`
          )}
          label={dictionary.sixthSectionWitnessFirstInput}
          onChange={(event: any) => {
            validateLettersOnly(event);
          }}
          sx={styleTextField}
        />
        <TextField
          role={Boolean(witnessError?.lastName) ? 'alert' : undefined}
          inputProps={{
            maxLength: '30',
          }}
          id={`${dictionary.sixthSectionWitnessSecondInput}-${index}`}
          error={Boolean(witnessError?.lastName)}
          helperText={
            Boolean(witnessError?.lastName?.message) &&
            generateError(
              dictionary.sixthSectionWitnessSecondInput,
              witnessError?.lastName?.message!
            )
          }
          {...register(
            `${witnessBaseName}.lastName` as `caseDetails.0.witnesses.0.lastName`
          )}
          fullWidth
          label={dictionary.sixthSectionWitnessSecondInput}
          onChange={(event: any) => validateLettersOnly(event)}
          sx={styleTextField}
        />
        <ControllerSelect
          name={`${witnessBaseName}.typeId` as const}
          label={dictionary.sixthSectionWitnessThirdInput}
        >
          {witnessesType.map(({ value, id }) => (
            <MenuItem key={id} value={id}>
              {value}
            </MenuItem>
          ))}
        </ControllerSelect>
        <PhoneNumber
          phoneNumberError={Boolean(witnessError?.phone)}
          phoneNumberFormName={`${witnessBaseName}.phone` as const}
          phoneNumberPrefixFormName={`${witnessBaseName}.phonePrefixRelationId`}
          phoneNumberLabel={dictionary.sixthSectionWitnessFourthInput}
          phoneNumberErrorMsg={witnessError?.phone?.message}
        />
        <TextField
          role={Boolean(witnessError?.role) ? 'alert' : undefined}
          inputProps={{
            maxLength: '50',
          }}
          id={`${dictionary.sixthSectionWitnessFifthInput}-${index}`}
          error={Boolean(witnessError?.role)}
          helperText={
            Boolean(witnessError?.role?.message) &&
            generateError(
              dictionary.sixthSectionWitnessFifthInput,
              witnessError?.role?.message!
            )
          }
          {...register(
            `${witnessBaseName}.role` as `caseDetails.0.witnesses.0.role`
          )}
          label={dictionary.sixthSectionWitnessFifthInput}
          onChange={(event: any) => validateLettersAndNumbersOnly(event)}
          sx={styleTextField}
        />
        <Stack>
          <TextField
            role={Boolean(witnessError?.civilAddress) ? 'alert' : undefined}
            inputProps={{
              maxLength: '100',
            }}
            id={`${dictionary.sixthSectionWitnessCivilAddress}-${index}`}
            error={Boolean(witnessError?.civilAddress)}
            helperText={
              Boolean(witnessError?.civilAddress?.message) &&
              generateError(
                dictionary.sixthSectionWitnessCivilAddress,
                witnessError?.civilAddress?.message!
              )
            }
            {...register(
              `${witnessBaseName}.civilAddress` as `caseDetails.0.witnesses.0.civilAddress`
            )}
            label={dictionary.sixthSectionWitnessCivilAddress}
            {...textFieldShrink(civilAddress)}
            onKeyDown={(event: any) => validateLettersAndNumbersOnly(event)}
            disabled={hasNoCivilAddress}
            sx={styleTextField}
          />
          <CustomCheckbox
            name={`${witnessBaseName}.hasNoCivilAddress`}
            label={dictionary.fourthSectionNoInfo}
          />
        </Stack>
        <Stack gap='10px'>
          <Typography sx={{ fontWeight: 'bold' }}>
            {dictionary.fourthSectionWitnessFileAdd}
          </Typography>
          <LinkDownload
            text={dictionary.fourthSectionWitnessDownloadWitnessFile}
            callback={async () => {
              window.open(
                'https://shikum-umb.mod.gov.il/media/ciadbwtu/testimony.pdf',
                '_blank'
              );
            }}
          />
        </Stack>
        <Stack>
          <FileUpload
            caseDetailsId={props.caseId}
            witnessId={witnessId}
            fileLabel={dictionary.thirdSectionFileUploadWitnessText}
            fileCategoryId={FileCategories.WitnessDocument}
            ariaLabel={dictionary.thirdSectionThirdFileUploadWitnessLabel}
            disabled={hasNoWitnessDocument}
            dataRequired={!hasNoWitnessDocument}
          />
          {!isFileExist(filesList, FileCategories.WitnessDocument) && (
            <CustomCheckbox
              name={`${witnessBaseName}.hasNoWitnessDocument`}
              label={dictionary.fourthSectionNoInfo}
            />
          )}
        </Stack>
      </Grid>
    </PaperCard>
  );
};
